import { fieldConfig } from "@autoform/zod";
import { z } from "zod";

import { ENRICHMENT_STATUSES } from "@/lib/enrichment";

const disabled = fieldConfig({ inputProps: { disabled: true } });

const transliterationList = z
  .array(z.string())
  .nullish()
  .default([])
  .superRefine(fieldConfig({ fieldType: "tagList" }));

/**
 * Edit schema for a ShowGenre, bound to `ShowGenreDbDto` from `GET /api/show-genres/{id}` and
 * mapped onto `UpdateShowGenreCommand` by `useShowGenreRepository.update`.
 *
 * `sections` is stored as a string list (`cinema`, `theater`) but the command takes two booleans,
 * so the form shows `inCinemas`/`inTheaters` — see `toShowGenreForm`. The backend refuses to
 * untick the last one.
 *
 * `slug` is display-only: `UpdateShowGenreCommand` has no slug member, and the stored value is
 * derived from the name at create.
 */
export const showGenreEditSchema = z.object({
  id: z.string().nullish().superRefine(disabled),
  name: z.string().min(1, "Required"),
  slug: z.string().nullish().superRefine(disabled),
  about: z
    .string()
    .nullish()
    .superRefine(fieldConfig({ fieldType: "textarea" })),
  aliases: z
    .array(z.string())
    .nullish()
    .default([])
    .superRefine(fieldConfig({ fieldType: "tagList" })),
  parents: z
    .array(z.string())
    .nullish()
    .default([])
    .superRefine(fieldConfig({ fieldType: "tagList" })),
  inCinemas: z
    .boolean()
    .default(false)
    .superRefine(fieldConfig({ label: "In cinemas" })),
  inTheaters: z
    .boolean()
    .default(false)
    .superRefine(fieldConfig({ label: "In theaters" })),
  transliterations: z
    .object({
      en: transliterationList,
      el: transliterationList,
      ar: transliterationList,
      ro: transliterationList,
    })
    .nullish(),
  externalIds: z
    .object({
      athinorama: z.string().nullish(),
      tmdb: z.coerce.number().int().nullish(),
    })
    .nullish(),
  enrichmentStatus: z
    .enum(ENRICHMENT_STATUSES)
    .nullish()
    .superRefine(fieldConfig({ fieldType: "enrichmentStatus" })),
  createdBy: z.string().nullish().superRefine(disabled),
  createdAt: z
    .string()
    .datetime({ offset: true })
    .nullish()
    .superRefine(
      fieldConfig({ fieldType: "datetime", inputProps: { disabled: true } }),
    ),
  updatedBy: z.string().nullish().superRefine(disabled),
  updatedAt: z
    .string()
    .datetime({ offset: true })
    .nullish()
    .superRefine(
      fieldConfig({ fieldType: "datetime", inputProps: { disabled: true } }),
    ),
});

export type ShowGenreEditFormType = z.infer<typeof showGenreEditSchema>;

/** Stored `sections` list → the two booleans the form and the command speak. */
export function toShowGenreForm(raw: unknown): unknown {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return raw;
  const record = raw as Record<string, unknown>;
  const sections = Array.isArray(record.sections) ? record.sections : [];
  return {
    ...record,
    inCinemas: sections.includes("cinema"),
    inTheaters: sections.includes("theater"),
  };
}
